import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { TImage } from '../types';
import Dropzone, {
  DropzoneImperativeHandle,
} from '../components/forms/form-elements/Dropzone';
import { reportProblem } from '../api/contacts';
import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import DescriptionInput from '../components/forms/form-elements/DescriptionInput';
import Button from '../components/buttons/Button';
import { useResponsiveWindow } from '../hooks/useResponsiveWindow';
import { yupResolver } from '@hookform/resolvers/yup';
import { signalProblemSchema } from '../validation/schemas/contacts';

interface IFormInput {
  reason: string;
}

const SignalProblem = () => {
  const [images, setImages] = useState<TImage[]>([]);

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { isValid, errors, isSubmitting },
  } = useForm<IFormInput>({
    defaultValues: {
      reason: '',
    },
    resolver: yupResolver(signalProblemSchema),
    mode: 'onChange',
  });

  const refDropzone = useRef<DropzoneImperativeHandle>();

  const { t } = useTranslation();

  const { isMobile } = useResponsiveWindow();

  const addImages = useCallback((newImages: TImage[]) => {
    setImages((prev) => [...prev, ...newImages]);
  }, []);

  const removeImage = useCallback((image: TImage) => {
    setImages((prev) => prev.filter((i) => i.id !== image.id));
  }, []);

  const clearImages = useCallback(() => {
    if (refDropzone.current) {
      refDropzone.current.clearImages();
    }

    setImages([]);
  }, []);

  const handleFormSubmit = async (values: IFormInput) => {
    const formData = {
      body: values.reason,
      files: images,
    };

    const { data, error } = await reportProblem(formData);

    if (error) {
      toast.error(error.error, {
        theme: 'dark',
        className: 'text-center',
        bodyClassName: 'custom-toast-body',
      });
      return;
    }

    if (data) {
      toast.success(t(COMMON_TRANSLATE_KEYS.SIGNAL_SUBMITTED), {
        theme: 'dark',
        className: 'text-center',
        bodyClassName: 'custom-toast-body',
      });
    }

    resetForm();
    clearImages();
  };

  return (
    <div className="flex flex-col px-8 items-center my-10 w-full h-full overflow-x-clip">
      <h1 className="text-mainWhite text-2xl text-center w-full md:w-[37rem] lg:w-[110%] font-bold lg:border-y-2 border-dividerGray py-3 px-8 justify-center">
        {t(COMMON_TRANSLATE_KEYS.SIGNAL_PROBLEM_DESCRIPTION)}
      </h1>
      <div className="flex flex-col gap-5 items-center w-full h-fit md:max-w-[37rem]">
        <div className="flex flex-col gap-1 md:w-fit w-full mt-10">
          <p className="text-mainWhite">
            {t(COMMON_TRANSLATE_KEYS.PROBLEM)}{' '}
            <span className="text-functionalRed">*</span>
          </p>
          <DescriptionInput
            error={t(errors.reason?.message || '')}
            {...register('reason')}
            placeholder={t(COMMON_TRANSLATE_KEYS.DESCRIBE_PROBLEM)}
            width={!isMobile ? "37rem" : "100%"}
            height={10}
          />
        </div>
        <div className="text-mainWhite flex flex-col gap-1 md:w-fit w-full">
          <p>{t(COMMON_TRANSLATE_KEYS.PHOTOS)}</p>
          <Dropzone
            containerWidth={isMobile ? '100%' : '37rem'}
            addImages={addImages}
            removeImage={removeImage}
            images={images}
            customDropzoneStyle={{
              height: '10rem',
              width: isMobile ? '100%' : '37rem',
              marginBottom: '1.25rem',
            }}
            maxFiles={5}
            showFullImageOnClick
          />
        </div>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          isDisabled={!isValid}
          isLoading={isSubmitting}
          loadingText={`${t(COMMON_TRANSLATE_KEYS.SENDING)}...`}
          backgroundColor="bg-functionalGreen"
          textColor="text-mainWhite"
          width={20}
          height={7}
        >
          {t(COMMON_TRANSLATE_KEYS.SEND)}
        </Button>
      </div>
    </div>
  );
};

export default SignalProblem;
